import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Grid,
} from "@mui/material";

const NewInvoicePrintTemplate = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();

  const { data = {}, tableData = [], navigateBack } = location.state || {};
  const decimalCount = useSelector((state) => state.common?.decimalCount) || 2;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(navigateBack || -1);
    },
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handlePrint();
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const themeColor = "#1a4675"; // Dark blue matching the image

  const getImageUrl = (prod) => {
    let rawImg = prod.photo || prod.image || prod.image_url || "";
    if (!rawImg) return "";

    try {
      // If it's a JSON string, parse it to extract the 'img' property
      if (typeof rawImg === "string" && rawImg.trim().startsWith("{")) {
        const parsed = JSON.parse(rawImg);
        return parsed.img || rawImg;
      }
    } catch (error) {
      console.log("Error parsing image JSON:", error);
    }

    return rawImg;
  };

  const getBatchValue = (batchVal) => {
    if (!batchVal) return "";
    try {
      if (typeof batchVal === "string" && batchVal.trim().startsWith("[")) {
        const parsed = JSON.parse(batchVal);
        if (Array.isArray(parsed)) {
          return parsed.join(", ");
        }
      }
    } catch (error) {
      console.log("Error parsing batch JSON:", error);
    }
    return batchVal;
  };

  const borderStyle = "1px solid #757575";

  return (
    <Box sx={{ display: "none" }}>
      <Box
        ref={componentRef}
        sx={{
          boxSizing: "border-box",
          p: "20px",
          fontFamily: "'Roboto', sans-serif",
          color: "black",
          width: "100%",
          maxWidth: "210mm",
          minHeight: "270mm",
          display: "flex",
          flexDirection: "column",
          m: "0 auto",
          bgcolor: "white",
        }}
      >
        {/* Header Section */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box sx={{ width: "25%" }}>
            <Typography
              sx={{
                fontSize: "36px",
                fontWeight: "900",
                color: themeColor,
                m: 0,
                lineHeight: 1,
              }}
            >
              ESTIMATE
            </Typography>
          </Box>

          <Box sx={{ width: "50%", textAlign: "center", pt: "20px" }}>
            {/* Empty middle space for header */}
          </Box>

          <Box
            sx={{
              width: "25%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Table
              size="small"
              sx={{
                width: "120px",
                border: borderStyle,
                "& .MuiTableCell-root": {
                  border: borderStyle,
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "13px",
                  p: "2px",
                  color: "black",
                },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell>DATE</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {data.BillDate
                      ? dayjs(data.BillDate).format("DD-MM-YYYY")
                      : ""}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Stack>

        {/* Customer Info Bar */}
        <Box
          sx={{
            bgcolor: themeColor,
            color: "white",
            p: "4px 8px",
            fontWeight: "bold",
            fontSize: "14px",
            mt: "5px",
            width: "100%",
            textTransform: "uppercase",
          }}
        >
          CUSTOMER INFO
        </Box>

        {/* Customer Info Text (Under the bar) */}
        <Box sx={{ width: "100%", textAlign: "center", pt: "5px", pb: "5px" }}>
          <Typography sx={{ fontWeight: "900", fontSize: "16px" }}>
            {data.BillPartyName || data.company_name}
          </Typography>
          <Typography
            sx={{
              fontWeight: "900",
              fontSize: "16px",
              textTransform: "uppercase",
            }}
          >
            {data.Address1
              ? `${data.Address1} ${data.Address2 || ""}`
              : "COIMBATURE"}
          </Typography>
        </Box>

        {/* Items Table */}
        <TableContainer
          sx={{
            mt: "15px",
            border: borderStyle,
            borderBottom: "none",
            overflow: "visible",
          }}
        >
          <Table
            sx={{
              "& .MuiTableCell-root": {
                border: borderStyle,
                p: "4px 5px",
                fontSize: "13px",
                color: "black",
              },
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  { label: "Sl no", width: "4%" },
                  { label: "Batch", width: "8%" },
                  { label: "Model No", width: "12%" },
                  { label: "PHOTO", width: "22%" },
                  { label: "Discription", width: "25%" },
                  { label: "Qty", width: "7%" },
                  { label: "Unit Price", width: "10%" },
                  { label: "Amount", width: "12%" },
                ].map((col, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      bgcolor: themeColor,
                      color: "white !important",
                      fontWeight: "bold",
                      textAlign: "center",
                      width: col.width,
                      lineHeight: 1.2,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((prod, index) => {
                const photoUrl = getImageUrl(prod);
                return (
                  <TableRow key={index}>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell align="center">
                      {getBatchValue(prod.batch || data.batch)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {prod.Size || prod.model_no || ""}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          width: "110px",
                          height: "80px",
                          m: "0 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt="Product"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <Typography sx={{ color: "#ccc", fontSize: "12px" }}>
                            No Image
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      {prod.ItemNameTextField || prod.description || ""}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {prod.qty}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {(Number(prod.Rate1) || 0).toFixed(decimalCount)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      {(Number(prod.total) || 0).toFixed(decimalCount)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ flexGrow: 1 }} />

        {/* Footer block - exactly aligning with the table columns */}
        <Box sx={{ border: borderStyle, marginTop: "auto" }}>
          <Grid container>
            {/* Left side balances */}
            <Grid item xs={8} sx={{ borderRight: borderStyle }}>
              {[
                {
                  label: "Cash Part",
                  value: data.CashAmount || data.cash_part || 0,
                },
                {
                  label: "Cheque Part",
                  value: data.ChequeAmount || data.cheque_part || 0,
                },
                { label: "Opening Balance", value: data.BalanceAmount || 0 },
                { label: "New Bill", value: data.BillNetTotalField || 0 },
                {
                  label: "Closing Balance",
                  value:
                    (data.BalanceAmount || 0) +
                    (data.BillNetTotalField || 0) -
                    (data.advance || 0),
                },
              ].map((row, idx) => (
                <Grid
                  container
                  key={idx}
                  sx={{ borderBottom: idx !== 4 ? borderStyle : "none" }}
                >
                  <Grid
                    item
                    xs={6}
                    sx={{
                      p: "6px",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontSize: "14px",
                    }}
                  >
                    {row.label}
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sx={{
                      p: "6px",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontSize: "14px",
                    }}
                  >
                    {Number(row.value || 0).toFixed(decimalCount) !== "0.00" ||
                    idx >= 2
                      ? Number(row.value || 0).toFixed(decimalCount)
                      : ""}
                  </Grid>
                </Grid>
              ))}
            </Grid>

            {/* Right side totals */}
            <Grid item xs={4}>
              <Grid container sx={{ height: "100%" }}>
                <Grid
                  item
                  xs={12}
                  sx={{ borderBottom: borderStyle, height: "33.33%" }}
                >
                  <Grid container sx={{ height: "100%" }}>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        p: "6px",
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: "14px",
                        borderRight: borderStyle,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      SUB TOTAL
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        p: "6px",
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {Number(data.BillTotalField || 0).toFixed(decimalCount)}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ borderBottom: borderStyle, height: "33.33%" }}
                >
                  <Grid container sx={{ height: "100%" }}>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        p: "6px",
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: "14px",
                        borderRight: borderStyle,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      TAX
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        p: "6px",
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {Number(data.TaxAmt || 0).toFixed(decimalCount)}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sx={{ height: "33.33%" }}>
                  <Grid container sx={{ height: "100%" }}>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        p: "6px",
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: "14px",
                        borderRight: borderStyle,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      NET TOTAL
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        p: "6px",
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {Number(data.BillNetTotalField || 0).toFixed(
                        decimalCount,
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default NewInvoicePrintTemplate;
